import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import {
  CompanyLocationContactPersonOutDTO,
  CompanyLocationExceptionPreviewOutDTO,
  CompanyLocationRampWithOpeningHoursOutDTO,
  CompanyLocationsDetailOutDTO,
  DayEnum,
  ExceptionTimeIntervalOutDTO,
  FeaturesEnum,
  LocationListPreviewOutDTO,
  OpeningHoursDayDTO,
  OpeningModeEnum,
  Pageable,
} from '@api/ringil-api.v2';
import { BlueIcon, HeadingRow, Note, RingilHeading } from '@components/styles.tsx';
import styled from 'styled-components';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  dayjsFromApiPrague,
  formatDateFromDayjsWithLocale,
  formatDateFromStringWithLocale,
  formatTimeFromDayjsWithLocale,
} from '@utils/dateWithTz';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { Chip } from 'primereact/chip';
import { InputText } from 'primereact/inputtext';
import useNavigation from '@hooks/useNavigation.tsx';
import { BadgeRow } from '@components/obsolete/Badge/Badge.tsx';
import VisibilityBadge from '@app/pages/customer/LocalityList/component/VisibilityBadge.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import AddEditLocationSidebar from '@app/pages/customer/LocalityList/sidebars/AddEditLocationSidebar.tsx';
import AddEditRamp from '@app/pages/customer/LocalityList/sidebars/AddEditRamp.tsx';
import EditOpeningHours from '@app/pages/customer/LocalityList/sidebars/EditOpeningHours.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import COText from '@app/pages/COText/COText.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import SvgCheck from '@icons/check.svg?react';
import SvgCross from '@icons/close.svg?react';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';

interface ComponentProps {
  localityId: string;
}

interface I18NLabels {
  i18n: string;
  value: string;
}

// TODO (legacy decommission): when this legacy page is removed and the new detail becomes the only version, also wire
// the new CreateLocationSidebar into LocalityList (replacing the legacy AddEditLocationSidebar) so create uses the new flow.
const LocalityDetailLegacy: React.FC<ComponentProps> = ({ localityId }) => {
  const { tr } = useTranslationLgs();
  const { getCompanyLocationById, getExceptionsForCompanyLocation, deleteRampForLocation, activateRampForLocation } = useContext(ApiContext);
  const [locationDetail, setLocationDetail] = useState<CompanyLocationsDetailOutDTO>();
  const [localityExceptions, setLocalityExceptions] = useState<CompanyLocationExceptionPreviewOutDTO[]>();
  const [visibleEditLocalityId, setVisibleEditLocalityId] = useState(undefined);
  const [visibleAddRamp, setVisibleAddRamp] = useState(false);
  const [ramp2Delete, setRamp2Delete] = useState(undefined);
  const [editRampId, setEditRampId] = useState(undefined);
  const [visibleEditOpeningHours, setVisibleEditOpeningHours] = useState(false);

  const { hasFeature } = useContext(AuthenticatedUserContext);

  const cmrSigningEnabled = hasFeature([FeaturesEnum.SIGNING_CMR]);

  const navigation = useNavigation();

  useEffect(() => {
    load();
  }, [localityId]);

  useEffect(() => {
    contactsTableRef?.current?.reloadData();
  }, [locationDetail?.contacts]);

  const load = () => {
    getCompanyLocationById(localityId, data => {
      setLocationDetail(data);
    });
    getExceptionsForCompanyLocation(localityId, data => {
      setLocalityExceptions(data);
    });
  };
  const renderOpeningTime = (isClosed: boolean, openingTime?: ExceptionTimeIntervalOutDTO[]) => {
    if (isClosed) return tr('ExceptionList.closed', 'Closed');
    return openingTime.map(
      (item, index) => {
        const startTime = isNotNullOrUndefined(item.localStartTime)
          ? item.localStartTime
          : formatTimeFromDayjsWithLocale(dayjsFromApiPrague(item.since))
        const endTime = isNotNullOrUndefined(item.localEndTime)
          ? item.localEndTime
          : formatTimeFromDayjsWithLocale(dayjsFromApiPrague(item.till))
        return `${startTime} - ${endTime}`
      }
    ).join(', ');
  };

  const renderLocalities = (localities: LocationListPreviewOutDTO[]) => {
    if (localities.length == 0) return tr('ExceptionList.allLocations', 'All locations');
    return localities.map((item, index) => <div key={index}>{item.localityName}</div>);
  };

  const renderRamps = (localities: LocationListPreviewOutDTO[]) => {
    if (localities.length == 0) return tr('ExceptionList.allRamps', 'All ramps');
    return localities.map((item, index) => (
      <RampList key={index}>
        {item.rampsNames.length == 0 ? tr('ExceptionList.allRamps', 'All ramps') : item.rampsNames.map((ramp, rIndex) => `${ramp}`).join(', ')}
      </RampList>
    ));
  };

  const renderOpeningHoursForDay = (day: OpeningHoursDayDTO) => {
    if (day == null) return <div></div>;
    if (day.openingMode === OpeningModeEnum.CLOSED) return <div>{tr(`LocalityDetail.closed`, `Closed`)}</div>;
    if (day.openingMode === OpeningModeEnum.OPEN) return <div>{tr(`LocalityDetail.allDay`, `non-stop`)}</div>;
    return day.openingHours.map((t, index) => (
      <div key={index}>
        {t.since} - {t.till}
      </div>
    ));
  };

  // confirmID of deleting ramp
  const [confirmDeletingOfRamp, setConfirmDeletingOfRamp] = useState(undefined);

  interface ActionNeeded {
    action: string;
    className: string;
    data: { count: number; localizedMessage: string };
  }

  const softDeleteRamp = (rampId: string) => {
    deleteRampForLocation(
      localityId,
      rampId,
      false,
      true,
      () => {
        load();
      },
      null,
      {
        onAdditionalAction: (data: ActionNeeded) => {
          if (data.action === 'FORCE_NEEDED') setConfirmDeletingOfRamp(rampId);
        },
      },
    );
  };

  const activateRamp = (locationId: string, rampId: string) => {
    activateRampForLocation(
      locationId,
      rampId,
      () => {
        load();
      },
    )
  };


  const contactsTableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const renderContacts = (a: I18NLabels[]) => {
    const b = a
      .filter(t => !isNullOrUndefined(t.value) && t.value !== '')
      .map(t => ({
        name: isNullOrUndefined(t.i18n) ? null : tr(`LocalityDetail.i18n.${t.i18n}`, `LocalityDetail.i18n.${t.i18n}`),
        value: t.value,
      }))
      .map(t => {
        const s = isNullOrUndefined(t.name) ? t.value : `${t.name}: ${t.value}`;
        return s;
      })
      .join(' | ');

    return b;
  };

  const loadContacts = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CompanyLocationContactPersonOutDTO>) => void) => {
    const page = pageable.pageNumber || 1;
    const size = pageable.pageSize || 10;
    const start = (page - 1) * size;
    const end = start + size;

    const mainContact: CompanyLocationContactPersonOutDTO | undefined = locationDetail && locationDetail.contactName
      ? {
        contactName: locationDetail.contactName,
        contactEmail: locationDetail.contactEmail,
        contactPhone: locationDetail.contactPhone,
        automaticPrefillToNotify: locationDetail.contactAutomaticPrefillToNotify,
      } as CompanyLocationContactPersonOutDTO
      : undefined;

    const contacts = [
      ...(mainContact ? [mainContact] : []),
      ...(locationDetail?.contacts || []),
    ];

    const items = contacts.slice(start, end);

    const pagedData: PagedData<CompanyLocationContactPersonOutDTO> = {
      data: items,
      total: locationDetail?.contacts?.length || 0,
      page,
      size,
    };

    onSuccess(pagedData);
  };

  const contactColumnsConfig: ColumnConfig<CompanyLocationContactPersonOutDTO>[] = [
    {
      id: 'contactName',
      header: 'LocalityDetail.contactName',
      headerDefault: 'Contact name',
      width: '25%',
      sortable: false,
      order: 1,
      render: (rowData: CompanyLocationContactPersonOutDTO) => (
        <span>{rowData.contactName}</span>
      ),
    },
    {
      id: 'contactEmail',
      header: 'LocalityDetail.contactEmail',
      headerDefault: 'Contact email',
      width: '40%',
      sortable: false,
      order: 2,
      render: (rowData: CompanyLocationContactPersonOutDTO) => (
        <span>{rowData.contactEmail}</span>
      ),
    },
    {
      id: 'contactPhone',
      header: 'LocalityDetail.contactPhone',
      headerDefault: 'Contact phone',
      width: '20%',
      sortable: false,
      order: 3,
      render: (rowData: CompanyLocationContactPersonOutDTO) => (
        <span>{rowData.contactPhone}</span>
      ),
    },
    {
      id: 'automaticPrefillToNotify',
      header: 'LocalityDetail.automaticPrefillToNotify',
      headerDefault: 'Automatic prefill to notify',
      width: '15%',
      sortable: true,
      field: 'arrival.date',
      order: 4,
      render: (rowData: CompanyLocationContactPersonOutDTO) => (
        <div className="flex items-center justify-start">
          {rowData.automaticPrefillToNotify
            ? <SvgCheck className="w-6 h-6 text-complementary-success" /> :
            <SvgCross className="text-complementary-error" />}
        </div>
      ),
    },
  ];

  return (
    <>
      <BreadCrumbComponent
        items={[
          {
            label: tr(`index.localityList`, `Locality list`),
            url: navigation.createNavigationLink(navigation.urlFunctions.createLocationsList()),
          },
          {
            label: locationDetail?.name ?? tr(`LocalityDetail.detail`, `Detail`),
            disabled: true,
          },
        ]}
      />
      {locationDetail && (
        <>
          <HeadingRow>
            <RingilHeading>{locationDetail.name}</RingilHeading>

            {/* A deleted location (a superseded one is deleted too) is read-only; Restore is the only way back. */}
            {!locationDetail.isDeleted && (
              <Gapped>
                {!locationDetail.isPartnerLocation && (
                  <Button label={tr(`LocalityDetail.newRamp`, `New ramp`)} icon={'pi pi-plus'}
                          onClick={e => setVisibleAddRamp(true)} />
                )}
                <Button
                  label={tr(`LocalityDetail.updateLocality`, `Update locality`)}
                  onClick={e => {
                    setVisibleEditLocalityId(locationDetail.id);
                  }}
                />
              </Gapped>
            )}
          </HeadingRow>
          <BadgeRow>
            <VisibilityBadge acl={locationDetail.acl} />
          </BadgeRow>
          {locationDetail.isDeleted && (
            <div>
              {locationDetail.supersededBy
                ? tr('LocalityDetail.supersededMessage', 'This location was replaced by {{name}}.', { name: locationDetail.supersededBy.name })
                : tr('LocalityDetail.deletedReadOnlyMessage', 'This location is deleted and cannot be edited. Restore it to make changes.')}
            </div>
          )}
          <AddressRow>
            <Adress>
              {locationDetail.streetNr}, {locationDetail.postalCode}, {locationDetail.city}, {locationDetail.country}
            </Adress>
          </AddressRow>
          <div>
            {tr(`LocalityDetail.concurrentlyActiveEventsCount`, `concurrentlyActiveEventsCount: {{concurrentlyActiveEventsCount}}`, {
              concurrentlyActiveEventsCount: locationDetail.concurrentlyActiveEventsCount,
            })}
          </div>
          <HorizontalFieldValue
            label={tr(`LocalityDetail.timezoneId`, `Timezone Id`)}
            value={locationDetail.timezoneId}
          />
          <HorizontalFieldValue label={tr(`LocalityDetail.locationId`, `Location Id`)} value={locationDetail.id} />

          {(locationDetail.externalIds?.length > 0 || locationDetail.externalId) && (
            <HorizontalFieldValue
              label={tr(`LocalityDetail.locationExternalIds`, `External location IDs`)}
              value={locationDetail.externalIds?.length > 0 ? locationDetail.externalIds.join(', ') : locationDetail.externalId}
            />
          )}

          <COHeadline title={tr(`LocalityDetail.contacts2`, `Contacts`)} variant={'h2'} />
          <RingilDataTable
            ref={contactsTableRef}
            columnConfig={contactColumnsConfig as ColumnConfig<unknown>[]}
            loadDataFromApi={loadContacts}
            filterName={'location_contacts_list'}
          />
          <TableHeading>
            <COHeadline title={tr(`LocalityDetail.openingHours`, `Opening hours`)} variant={'h2'} />
            <div>
              {!locationDetail.isDeleted && (
                <Button
                  label={tr(`LocalityDetail.updateOpeningHours`, `Update opening hours`)}
                  className={'p-button-outlined'}
                  onClick={e => setVisibleEditOpeningHours(true)}
                />
              )}
            </div>
          </TableHeading>

          <TableWrapper>
            <div className={'p-datatable p-component p-datatable-responsive-stack'}>
              <div className="p-datatable-wrapper">
                <table className="p-datatable-table" role="table">
                  <thead className="p-datatable-thead">
                  <tr role="row">
                    {locationDetail.openingHourDefinitions.openingHoursDays.map((ohd, ohIndex) => {
                      return (
                        <th className="" role="columnheader" key={ohIndex}>
                          <div className="p-column-header-content">
                            <span
                              className="p-column-title">{tr(`TimeIntervalComponent.days${ohd.day}`, ohd.day)}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                  </thead>
                  <tbody className="p-datatable-tbody">
                  {!(locationDetail.openingHourDefinitions?.openingHoursDays.length > 0) ? (
                    <tr className="p-datatable-emptymessage" role="row">
                      <td colSpan={7} role="cell">
                        No available options
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      {locationDetail.openingHourDefinitions.openingHoursDays.map((ohd, ohIndex) => {
                        return (
                          <td role={'cell'} key={ohIndex}>
                            {renderOpeningHoursForDay(ohd)}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          </TableWrapper>

          <TableHeading>
            <COHeadline title={tr(`LocalityDetail.exceptionsInNext30Days`, `Exceptions in next 30 days`)}
                        variant={'h2'} />
            <div>
              <Button
                label={tr(`LocalityDetail.manageExceptions`, `Manage exceptions >`)}
                className={'p-button-outlined'}
                onClick={e => navigation.navigate(navigation.urlFunctions.createExceptionList())}
              />
            </div>
          </TableHeading>
          <TableWrapper>
            <DataTable value={localityExceptions}
                       emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}>
              <Column
                style={{ width: '9rem' }}
                header={tr(`LocalityDetail.dateFrom`, `Date from`)}
                body={(row: CompanyLocationExceptionPreviewOutDTO) =>
                  row.localDates ? (
                    <div>
                      {row.localDates.map((t, index) => (
                        <div key={index}>{formatDateFromStringWithLocale(t)}</div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {row.dates.map((t, index) => (
                        <div key={index}>{formatDateFromDayjsWithLocale(dayjsFromApiPrague(t))}</div>
                      ))}
                    </div>
                  )
                }
              ></Column>
              <Column
                header={tr(`LocalityDetail.exception`, `Exception`)}
                body={(row: CompanyLocationExceptionPreviewOutDTO) => (
                  <div>
                    <div>{renderOpeningTime(row.closed, row.openingHours)}</div>
                    <div>{row.exceptionName}</div>
                  </div>
                )}
              ></Column>
              <Column
                header={tr(`LocalityDetail.locality`, `Locality`)}
                body={(row: CompanyLocationExceptionPreviewOutDTO) => <div>{renderLocalities(row.localities)}</div>}
              ></Column>
              <Column
                header={tr(`LocalityDetail.ramps`, `Ramps`)}
                body={(row: CompanyLocationExceptionPreviewOutDTO) => <div>{renderRamps(row.localities)}</div>}
              ></Column>
              <Column
                header={tr(`LocalityDetail.createdBy`, `Created by`)}
                body={(row: CompanyLocationExceptionPreviewOutDTO) => (
                  <div>
                    <div>{row.createdBy}</div>
                    <div>{formatDateFromDayjsWithLocale(dayjsFromApiPrague(row.createdAt))}</div>
                  </div>
                )}
              ></Column>
            </DataTable>
          </TableWrapper>
          {!locationDetail.isPartnerLocation && (
            <>
              <TableHeading>
                <COHeadline title={tr(`LocalityDetail.ramps`, `Ramps`)} variant={'h2'} />
              </TableHeading>
              <TableWrapper>
              <DataTable value={locationDetail.ramps}
                         emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}>
                <Column
                  header={tr(`LocalityDetail.ramp`, `Ramp`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>
                      <div className={'flex flex-row gap-2'}>
                        {row.name}
                        {row.isSoftDeleted && (
                          <COBadge variant={'filled'} type={'neutral'}>
                            <COText label={tr('LocalityDetail.smazano', 'Smazáno')} />
                          </COBadge>
                        )}
                      </div>

                      {row.isPrivate && <Chip label={tr(`LocalityDetail.privateRamp`, `Private`)}></Chip>}
                      {row.isShowOnRampDashboard && <Chip label={tr(`LocalityDetail.showOnPanel`, `on panel`)}></Chip>}
                      <Note>{row.rampId}</Note>
                      <Note>{row.timezoneId}</Note>
                    </div>
                  )}
                  filter={true}
                  filterElement={() => <InputText />}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.mo`, `Mo`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.MONDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.tu`, `Tu`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.TUESDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.we`, `We`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.WEDNESDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.th`, `Th`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.THURSDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.fr`, `Fr`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.FRIDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.sa`, `Sa`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.SATURDAY))}</div>
                  )}
                ></Column>
                <Column
                  header={tr(`LocalityDetail.day.su`, `Su`)}
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) => (
                    <div>{renderOpeningHoursForDay(row.openingHourDefinitions.openingHoursDays.find(t => t.day === DayEnum.SUNDAY))}</div>
                  )}
                ></Column>
                <Column
                  body={(row: CompanyLocationRampWithOpeningHoursOutDTO) =>
                    locationDetail.isDeleted ? null : (
                    <div>
                      <Link>
                        <BlueIcon className={'pi pi-pencil'} onClick={e => setEditRampId(row.rampId)} />
                      </Link>
                      {!row.isSoftDeleted && (
                        <Link>
                          <BlueIcon className={'pi pi-eye-slash'} onClick={e => softDeleteRamp(row.rampId)} />
                        </Link>
                      )}
                      {row.isSoftDeleted && (
                        <Link>
                          <BlueIcon className={'pi pi-eye'} onClick={e => activateRamp(localityId, row.rampId)} />
                        </Link>
                      )}
                    </div>
                    )
                  }
                ></Column>
              </DataTable>
              </TableWrapper>
            </>
          )}

          {cmrSigningEnabled && (
            <div className={'flex flex-col mt-1 gap-2'}>
              <COHeadline title={tr(`LocalityDetail.singingConfiguration`, `Signing configuration`)} variant={'h2'} />
              <COHeadline title={tr(`LocalityDetail.singingConfigurationSender`, `Sender`)} variant={'h3'} />
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationSenderEnabled`, `Singing enabled`)}
                                 value={locationDetail.senderSigningEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationSenderTextEnabled`, `Text enabled`)}
                                 value={locationDetail.senderSigningData?.textEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              {locationDetail.senderSigningData?.textEnabled && locationDetail.senderSigningData?.text && (
                <HorizontalFieldValue label={tr(`LocalityDetail.singingConfigurationSenderText`, `Text`)}
                                      value={locationDetail.senderSigningData?.text} />
              )}
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationSenderImageEnabled`, `Image enabled`)}
                                 value={locationDetail.senderSigningData?.imageEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              {locationDetail.senderSigningData?.imageEnabled && locationDetail.senderSigningData?.imageDataFileName && (

                <div className="font-medium"
                     style={{ lineBreak: 'auto', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {locationDetail.senderSigningData.imageDataFileName}
                </div>

              )}
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationSenderUserEnabled`, `User enabled`)}
                                 value={locationDetail.senderSigningData?.userEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              <COHeadline title={tr(`LocalityDetail.singingConfigurationReceiver`, `Receiver`)} variant={'h3'} />
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationReceiverEnabled`, `Singing enabled`)}
                                 value={locationDetail.receiverSigningEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationReceiverTextEnabled`, `Text enabled`)}
                                 value={locationDetail.receiverSigningData?.textEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              {locationDetail.receiverSigningData?.textEnabled && locationDetail.receiverSigningData?.text && (
                <HorizontalFieldValue label={tr(`LocalityDetail.singingConfigurationReceiverText`, `Text`)}
                                      value={locationDetail.receiverSigningData?.text} />
              )}
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationReceiverImageEnabled`, `Image enabled`)}
                                 value={locationDetail.receiverSigningData?.imageEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
              {locationDetail.receiverSigningData?.imageEnabled && locationDetail.receiverSigningData?.imageDataFileName && (

                <div className="font-medium"
                     style={{ lineBreak: 'auto', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {locationDetail.receiverSigningData.imageDataFileName}
                </div>

              )}
              <RingilInputSwitch label={tr(`LocalityDetail.singingConfigurationReceiverUserEnabled`, `User enabled`)}
                                 value={locationDetail.receiverSigningData?.userEnabled}
                                 onChange={v => {
                                 }}
                                 disabled
              />
            </div>
          )}

        </>
      )}

      <AddEditLocationSidebar
        visible={!!visibleEditLocalityId}
        onHide={() => setVisibleEditLocalityId(undefined)}
        onCompleted={() => {
          setVisibleEditLocalityId(undefined);
          load();
        }}
        locationId={visibleEditLocalityId}
        country={locationDetail?.country}
        timezoneId={locationDetail?.timezoneId}
      />

      <AddEditRamp
        visible={visibleAddRamp}
        onHide={() => setVisibleAddRamp(false)}
        onCompleted={() => {
          load();
          setVisibleAddRamp(false);
        }}
        locationId={localityId}
        timezoneId={locationDetail?.timezoneId}
      />

      <AddEditRamp
        visible={!!editRampId}
        onHide={() => setEditRampId(undefined)}
        onCompleted={() => {
          load();
          setEditRampId(undefined);
        }}
        locationId={localityId}
        timezoneId={locationDetail?.timezoneId}
        editRampId={editRampId}
      />

      <EditOpeningHours
        visible={visibleEditOpeningHours}
        onHide={() => setVisibleEditOpeningHours(false)}
        onCompleted={() => {
          load();
          setVisibleEditOpeningHours(false);
        }}
        locationId={localityId}
      />
    </>
  );
};

const RampList = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 1;
`;

const TableHeading = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
`;
const TableWrapper = styled.div`
  display: flex;

  .p-datatable .p-datatable-tbody > tr > td {
    padding: 1rem 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Adress = styled.div`
  display: flex;
`;
const AddressRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Gapped = styled.div`
  display: flex;
  gap: 1rem;
`;

const Link = styled.a`
  cursor: pointer;
`;

export default LocalityDetailLegacy;
